import { CalendarRange, ChevronDown, Layers } from 'lucide-react';
import { useState } from 'react';
import { t } from '@/lib/i18n';
import { useMe } from '@/lib/me';
import { useScope } from '@/lib/scope';
import { Button, Combobox, DateRangePicker, Field, Sheet } from '@/ui';
import s from './TopBar.module.css';

/** Project scope: one, several or all projects, in `?p=` (IA §1.3). */
export function ScopePicker({ block }: { block?: boolean }) {
  const me = useMe();
  const scope = useScope();
  return (
    <Combobox
      multiple
      icon={Layers}
      className={block ? s.block : s.scope}
      value={scope.projectIds}
      onChange={scope.setProjects}
      placeholder={t('All projects')}
      searchPlaceholder={t('Find a project…')}
      aria-label={t('Project scope: {scope}', { scope: scope.projectLabel })}
      options={me.projects.map((p) => ({ value: p.id, label: p.name, description: p.code }))}
      emptyText={t('No projects')}
    />
  );
}

/** Global time range: `?range=` or `?from=&to=`. */
export function TimeRange() {
  const scope = useScope();
  return (
    <DateRangePicker
      value={{ key: scope.range.key, fromDay: scope.range.fromDay, toDay: scope.range.toDay }}
      label={scope.rangeLabel}
      onChange={scope.setRange}
    />
  );
}

/** Phones: scope and time as one chip that opens a sheet (IA §4). */
export function ScopeChip() {
  const scope = useScope();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={s.scopeChip} onClick={() => setOpen(true)} aria-label={t('Scope and time range')}>
        <CalendarRange aria-hidden />
        <span className={s.scopeChipText}>
          {scope.projectLabel} · {scope.rangeLabel}
        </span>
        <ChevronDown aria-hidden />
      </button>
      <Sheet
        open={open}
        onOpenChange={setOpen}
        title={t('Scope and time range')}
        side="bottom"
        footer={
          <Button variant="primary" onClick={() => setOpen(false)}>
            {t('Done')}
          </Button>
        }
      >
        <div className={s.sheetFields}>
          <Field label={t('Projects')}>{() => <ScopePicker block />}</Field>
          <Field label={t('Time range')}>{() => <TimeRange />}</Field>
        </div>
      </Sheet>
    </>
  );
}
